import React, { ReactElement, useState } from 'react';
import { useIntl } from 'react-intl';
import Table, {
  TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@fuww/library/src/Table';
import ContainerScrollable from '@fuww/library/src/ContainerScrollable';
import { StyledChip } from '@fuww/library/src/Chip';
import SpacingContainer from '@fuww/library/src/SpacingContainer';
import IntelligencePageLayout from '@fuww/library/src/Layouts/IntelligencePage';
import Layout from '../components/Layout';
import messages from '../lib/messages.json';
import Head from '../components/Head';
import Hero from '../components/Hero';
import richestPeopleInFashion
  from '../data/staticPages/i/richest-people-in-fashion.json';
import formatBigMoney from '../lib/formatBigMoney';
import generateIntelligencePageBreadcrumbs
  from '../lib/generateIntelligencePageBreadcrumbs';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/9iNbaCocaOfOunKzC0C-7G6Pj8omZBoQ3ZYDTyq_QAI/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/w0RvfaL_-fHaQelidaxyC0xqLGXHbrINhNSxL_rhBOI/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/go6XgtJzOTF2ZPOuqoL120BMXCfdKYvTtZAo2TZaU_Q/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfbW9iaWxlLmpwZw',
  ],
  desktop: [
    'https://r.fashionunited.com/bFo3bLCFq3ED8ngOWFW_u4Sf1WBnqchR2bvhKaLiZgc/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/zSskrh6XOIeHKIs9dwX6AAR0LD2dLrhnl3cQycSfOe8/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/30Cf6g2SgtQiradsRe1fzwkX2RXRp0S8RT2MTlM01fg/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/Mgij_CmS6imkt8GT5GjhfunujMdTURziUg2A9_t3usg/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvcmljaGVzdF9wZW9wbGVfaW5fZmFzaGlvbl9iYW5uZXJfZGVza3RvcC5qcGc',
  ],
};

const years = [
  {
    value: 2015,
    label: '2015',
  },
  {
    value: 2016,
    label: '2016',
  },
  {
    value: 2017,
    label: '2017',
  },
];

const RichestPeopleInFashionPage = () => {
  const intl = useIntl();
  const [selectedYear, setSelectedYear] = useState(2017);

  const breadcrumbs = generateIntelligencePageBreadcrumbs(
    intl,
    'richest-people-in-fashion',
    'richestPeopleInFashion',
  );

  return (
    <>
      <Head
        title={intl.formatMessage(messages['richestPeopleInFashion.title'])}
        description={intl.formatMessage(
          messages['richestPeopleInFashion.description'],
        )}
      />

      <IntelligencePageLayout
        description={
          intl.formatMessage(
            messages['richestPeopleInFashion.description'],
          )
        }
        content={(
          <>
            <ContainerScrollable>
              {years.map(({ value, label }) => (
                <StyledChip
                  key={value}
                  label={label}
                  onClick={() => setSelectedYear(value)}
                  variant={selectedYear === value ? 'filled' : 'outlined'}
                />
              ))}
            </ContainerScrollable>
            <TableContainer>
              <Table
                aria-label={intl.formatMessage(
                  messages['richestPeopleInFashion.table.ariaLabel'],
                )}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{intl.formatMessage(messages.name)}</TableCell>
                    <TableCell>
                      {intl.formatMessage(messages.company)}
                    </TableCell>
                    <TableCell align="right">
                      {`${intl.formatMessage(
                        messages.netWorth,
                      )} ${selectedYear}`}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {richestPeopleInFashion.map(({
                    name, company, netWorth,
                  }, index) => (
                    <TableRow key={name}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{name}</TableCell>
                      <TableCell>{company}</TableCell>
                      <TableCell align="right">
                        {formatBigMoney(
                          (netWorth as Record<string, number | null>)[
                            selectedYear
                          ],
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <SpacingContainer />
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{
              __html: intl.formatMessage(messages[
                'richestPeopleInFashion.source'
              ]),
            }}
            />
          </>
        )}
        breadcrumbs={breadcrumbs}
        subTitle={intl.formatMessage(
          messages['richestPeopleInFashion.subTitle'],
        )}
        title={intl.formatMessage(messages['richestPeopleInFashion.title'])}
      />
    </>
  );
};

RichestPeopleInFashionPage.getLayout = (page: ReactElement) => (
  <Layout header={(
    <Hero
      alt="Richest people in fashion header image"
      imageUrls={headerImageUrls}
    />
  )}
  >
    {page}
  </Layout>
);

RichestPeopleInFashionPage.getInitialProps = redirectRouteToDomain(
  'richest-people-in-fashion',
);

export default RichestPeopleInFashionPage;
